import { EditUerForm } from 'components/EditUerForm';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import { UerChapter, UerClassifier } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    WorkOrderChangePriceData,
    ChapterEnum,
    PriceListUerRowUpdateInput,
    WorkOrderChangePriceItem,
} from 'gql/graphql';
import { PriceConstructionTypeRelationStatusEnum, ProjectMethodologyEnum, UerRecordStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useContractConstructionTypes } from 'pages/Project/Common/Contract/hooks/useContractConstructionTypes';
import { useCurrentEstimateBasis } from 'pages/Project/UerProject/Uer/CreateOrEditUer/hooks/useCurrentEstimateBasis';
import { useGetUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/hooks/useGetUer';
import { useParseUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/hooks/useParseUer';
import { prepareUpdateUerData } from 'pages/Project/UerProject/Uer/CreateOrEditUer/prepareAddUer';
import { formatNumberToRu } from 'utils';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { updateWorkOrderChangeUerGql } from '../WorkOrderChangeQueries';

const fieldsToCheck = {
    0: ['code', 'name', 'unit', 'basePrice.unitCost', 'uerClassifier', 'chapter', 'justification'],
};
const uerChaptersItems = Object.entries(UerChapter).map(([key, value]) => ({ id: key, label: value }));
const uerClassifierItems = Object.entries(UerClassifier).map(([key, value]) => ({ id: key, label: value }));

export const EditWorkOrderChangeUerDialog = ({
    isOpened,
    closeModalHandler,
    checkedUerRow,
    constructionTypeId,
    chapter,
    refreshTable,
    catalogName,
}: {
    isOpened: boolean;
    closeModalHandler: () => void;
    refreshTable: (objectIds: number[]) => void;
    // TODO: поправить типы после перехода на новый изм
    checkedUerRow: WorkOrderChangePriceData | WorkOrderChangePriceItem;
    constructionTypeId: number;
    chapter: ChapterEnum | undefined | null;
    catalogName?: string;
}) => {
    const dispatch = useDispatch();
    const { uerId, workOrderPriceId, workOrderChangePriceId } = checkedUerRow;
    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);
    const { contractId } = projectData ?? {};

    const hasWorkKind = useMemo(
        () => Boolean(projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind),
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind],
    );

    const {
        formState: { isDirty, errors },
        control,
        handleSubmit,
        reset,
        getValues,
        setValue,
        watch,
    } = useForm();
    const currentFormValues = getValues();

    const uerCode = useWatch({ control, name: 'code' });
    const selectedChapter = useWatch({ control, name: 'chapter' });
    const selectedUerClassifier = useWatch({ control, name: 'uerClassifier' });
    const ratioGroupIdsControl = useMemo(() => ({ control, name: 'ratioGroupIds' }), [control]);
    const ratioIdsControl = useMemo(() => ({ control, name: 'ratioIds' }), [control]);
    const dismantlingCoefControl = useMemo(() => ({ control, name: 'dismantlingCoefIds' }), [control]);

    const defaultChapterItem = useMemo(
        () => uerChaptersItems.find((itemChapter) => itemChapter.id === chapter),
        [chapter],
    );

    const [checkboxValues, setCheckboxValues] = useState<{ [key: string]: boolean }>(
        constructionTypeId ? { [constructionTypeId]: true } : {},
    );
    const typeTabCount = useMemo(() => {
        const count = Object.keys(checkboxValues).reduce(
            (sum, key) => sum + (checkboxValues[key] && currentFormValues[key]?.unitCost ? 1 : 0),
            0,
        );

        return count || '';
    }, [checkboxValues, currentFormValues]);
    const formItems = useMemo(
        () => [
            { id: 0, label: 'Общие данные' },
            { id: 1, label: `Виды строительства ${typeTabCount}` },
            ...(hasWorkKind ? [{ id: 2, label: 'Вид работ' }] : []),
        ],
        [hasWorkKind, typeTabCount],
    );

    const [activeFormTab, setActiveFormTab] = useState(formItems[0]);
    const [selectedFile, setSelectedFile] = useState<File[] | undefined>(undefined);

    useEffect(() => {
        setValue('chapter', defaultChapterItem);
    }, [defaultChapterItem, setValue]);

    const [contractConstructionTypes] = useContractConstructionTypes(contractId, ProjectMethodologyEnum.Uer);

    const [uerRowData, uerRowDataStatus, resetUerRowHandler, ratioItemsUsedIds] = useGetUer({
        uerId,
        reset,
    });
    const { status: uerRowItemStatus } = uerRowData?.uerPriceListUerRowWithRatio?.data || {};

    useEffect(() => {
        if (uerRowData) {
            const checkedCurrentPrices = uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices?.reduce(
                (acc: any, price) => ({
                    ...acc,
                    [price.constructionTypeId]: true,
                }),
                {},
            );

            if (R.isEmpty(checkedCurrentPrices)) {
                setCheckboxValues(
                    constructionTypeId
                        ? { [constructionTypeId]: true }
                        : { [contractConstructionTypes?.[0]?.id]: true },
                );
            } else {
                setCheckboxValues((checked) => ({ ...checked, ...checkedCurrentPrices }));
            }
        }
    }, [constructionTypeId, contractConstructionTypes, uerRowData]);

    const { currentEstimateBasisData, currentEstimateBasisRequest, resetCurrentEstimateBasis } =
        useCurrentEstimateBasis(setCheckboxValues);

    const {
        uerDataParseRequestSuccess,
        setUerDataParseRequestSuccess,
        parsedUerData,
        parsedUerStatus,
        parsedUerRequest,
        resetParsedDataHandler,
        onCloseParseErrorModal,
        wrongParseUerParameters,
    } = useParseUer({
        reset,
        setValue,
        checkboxValues,
        uerRowData: uerRowData?.uerPriceListUerRowWithRatio?.data as any,
        uerId,
        getValues,
        catalogName,
    });

    const areConstructionTypesCheckedAndDisabled = useMemo(
        () => uerRowItemStatus === UerRecordStatusEnum.ContractCondition,
        [uerRowItemStatus],
    );

    const isConstructionTypesContractCondition = uerRowItemStatus === UerRecordStatusEnum.ContractCondition;
    const isConstructionTypesAdditionalCondition = uerRowItemStatus === UerRecordStatusEnum.AdditionalCondition;
    const isConstructionTypesNewRecord = uerRowItemStatus === UerRecordStatusEnum.NewRecord;

    const areControlsDisabled =
        (!uerId && (parsedUerStatus !== RequestStatus.SUCCESS || !parsedUerData?.result)) ||
        (!!uerId && (parsedUerStatus !== RequestStatus.SUCCESS || !parsedUerData?.result));

    const [resetUpdateResultData] = useResetStoreData({ requestId: 'updateWorkOrderChangeUer' });

    const resetModal = useCallback(() => {
        resetCurrentEstimateBasis(undefined);
        setSelectedFile(undefined);
        resetParsedDataHandler(undefined);
        resetUerRowHandler(undefined);
        setCheckboxValues(constructionTypeId ? { [constructionTypeId]: true } : {});
        reset({ chapter: defaultChapterItem, code: '', justification: '' });
        resetUpdateResultData(undefined);
    }, [
        constructionTypeId,
        defaultChapterItem,
        reset,
        resetCurrentEstimateBasis,
        resetParsedDataHandler,
        resetUerRowHandler,
        resetUpdateResultData,
    ]);

    const base64Files = useReadFilesAsBase64(selectedFile);

    useEffect(() => {
        if (base64Files?.length) {
            dispatch(
                parsedUerRequest({
                    contractId,
                    fileContent: base64Files[0],
                    chapter: getValues().chapter?.id,
                    uerClassifier: getValues().uerClassifier?.id ?? null,
                }),
            );
        }
    }, [base64Files, contractId, dispatch, getValues, parsedUerRequest]);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            const isChapterUpdated =
                name === 'chapter' &&
                type === 'change' &&
                selectedChapter?.id &&
                selectedChapter.id !== value.chapter.id;
            const isUerClassifierUpdated =
                name === 'uerClassifier' &&
                type === 'change' &&
                (selectedUerClassifier?.id ?? null) !== (value.uerClassifier?.id ?? null);

            if (!isChapterUpdated && !isUerClassifierUpdated) {
                return;
            }

            if (base64Files?.length) {
                dispatch(
                    parsedUerRequest({
                        contractId,
                        fileContent: base64Files[0],
                        chapter: value.chapter?.id,
                        uerClassifier: value.uerClassifier?.id ?? null,
                    }),
                );
            } else if (
                isChapterUpdated &&
                (isConstructionTypesAdditionalCondition ||
                    uerRowData?.uerPriceListUerRowWithRatio?.data?.status === UerRecordStatusEnum.NewRecord)
            ) {
                dispatch(
                    currentEstimateBasisRequest({
                        uerBaseRowId: Number(uerId),
                        chapter: value.chapter.id,
                    }),
                );
            }
        });

        return () => subscription.unsubscribe();
    }, [
        base64Files,
        contractId,
        currentEstimateBasisRequest,
        dispatch,
        isConstructionTypesAdditionalCondition,
        parsedUerRequest,
        selectedChapter,
        selectedUerClassifier,
        uerId,
        uerRowData?.uerPriceListUerRowWithRatio?.data?.status,
        watch,
    ]);

    useEffect(
        () => () => {
            resetModal();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const onTabChange = useCallback(({ value }) => setActiveFormTab(value), [setActiveFormTab]);

    const onUploadUerFile = useCallback(
        (files) => {
            if (files.length && contractId) {
                setUerDataParseRequestSuccess(false);
                setSelectedFile(files);
            }
        },
        [contractId, setUerDataParseRequestSuccess],
    );

    const onConstructionTypeCheckChange = useCallback(
        (id: number) =>
            ({ checked }: { checked: boolean }) => {
                if (
                    checked &&
                    (isConstructionTypesAdditionalCondition || (isConstructionTypesNewRecord && !base64Files?.[0])) &&
                    currentFormValues?.chapter?.id
                ) {
                    dispatch(
                        currentEstimateBasisRequest({
                            uerBaseRowId: Number(uerId),
                            chapter: currentFormValues?.chapter?.id,
                        }),
                    );
                } else {
                    setValue(
                        `${id}.unitCostWithOther`,
                        parsedUerData?.data?.currentPrices?.filter((price) => price?.constructionTypeId === id)[0]
                            ?.unitCostWithOther || '',
                    );
                }
                setCheckboxValues((prev: Record<number, boolean>) => ({ ...prev, [id]: checked }));
            },
        [
            base64Files,
            currentEstimateBasisRequest,
            currentFormValues?.chapter?.id,
            dispatch,
            isConstructionTypesAdditionalCondition,
            isConstructionTypesNewRecord,
            parsedUerData?.data?.currentPrices,
            setValue,
            uerId,
        ],
    );

    useEffect(() => {
        if (currentEstimateBasisData?.result) {
            const idsCurrentPriceContractCondition =
                uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices?.reduce((acc: number[], item) => {
                    if (
                        item.constructionTypeRelationStatus ===
                        PriceConstructionTypeRelationStatusEnum.ContractCondition
                    ) {
                        acc = [...acc, item.constructionTypeId];
                    }

                    return acc;
                }, []);

            const checkedIdConstructionTypes = Object.entries(checkboxValues).reduce((acc: any, [key, value]) => {
                if (value && !idsCurrentPriceContractCondition?.includes(Number(key))) {
                    acc = [...acc, key];
                }

                return acc;
            }, []);

            checkedIdConstructionTypes.forEach((id: string) => {
                setValue(
                    `${id}.unitCostWithOther`,
                    formatNumberToRu(
                        currentEstimateBasisData?.data?.currentPrices.find(
                            (currentPriceItem) => currentPriceItem.constructionTypeId === Number(id),
                        )?.unitCostWithOther,
                        2,
                    ),
                );
            });
        }
    }, [
        checkboxValues,
        currentEstimateBasisData?.data?.currentPrices,
        currentEstimateBasisData?.result,
        setValue,
        uerRowData?.uerPriceListUerRowWithRatio?.data?.currentPrices,
    ]);

    const disabledByConstructionType = useCallback(
        (id: any) =>
            (isConstructionTypesContractCondition || isConstructionTypesAdditionalCondition) &&
            (currentFormValues[id]?.constructionTypeRelationStatus ===
                PriceConstructionTypeRelationStatusEnum.AdditionalCondition ||
                currentFormValues[id]?.constructionTypeRelationStatus ===
                    PriceConstructionTypeRelationStatusEnum.ContractCondition ||
                constructionTypeId === id),
        [
            constructionTypeId,
            currentFormValues,
            isConstructionTypesAdditionalCondition,
            isConstructionTypesContractCondition,
        ],
    );

    const isDisableIfCreateNotFile = useMemo(
        () => !uerId && !parsedUerData?.data?.code,
        [parsedUerData?.data?.code, uerId],
    );

    const isDisableIfDraftNotFile = useMemo(
        () => !!uerId && !!workOrderPriceId && !parsedUerData?.data,
        [parsedUerData?.data, uerId, workOrderPriceId],
    );

    const isDisableIfEditNotPrice = useMemo(
        () => !!uerId && !workOrderPriceId && uerRowItemStatus === UerRecordStatusEnum.Draft && !parsedUerData?.data,
        [parsedUerData?.data, uerRowItemStatus, uerId, workOrderPriceId],
    );

    const errorsKeys = Object.keys(errors);
    const btnConfirmDisabled = useMemo(
        () => !!errorsKeys.length || (!isDirty && R.isEmpty(checkboxValues)),
        [checkboxValues, errorsKeys.length, isDirty],
    );

    const checkIsConstructionTypeEditable = useCallback(
        (id) => constructionTypeId === id || checkboxValues[id] || areConstructionTypesCheckedAndDisabled,
        [areConstructionTypesCheckedAndDisabled, checkboxValues, constructionTypeId],
    );
    const checkIsConstructionTypeCheckboxDisabled = useCallback(
        (id) =>
            disabledByConstructionType(id) ||
            constructionTypeId === id ||
            isDisableIfCreateNotFile ||
            isDisableIfDraftNotFile ||
            isDisableIfEditNotPrice,
        [
            constructionTypeId,
            disabledByConstructionType,
            isDisableIfCreateNotFile,
            isDisableIfDraftNotFile,
            isDisableIfEditNotPrice,
        ],
    );
    const checkIsConstructionTypeCheckboxChecked = useCallback(
        (id) =>
            checkboxValues[id] ||
            (currentFormValues[id]?.unitCostWithOther &&
                (areConstructionTypesCheckedAndDisabled || constructionTypeId === id || checkboxValues[id])),
        [areConstructionTypesCheckedAndDisabled, checkboxValues, constructionTypeId, currentFormValues],
    );
    const isChapterFieldDisabled = useMemo(
        () => !uerId || (!!uerId && !isConstructionTypesAdditionalCondition && !uerDataParseRequestSuccess),
        [isConstructionTypesAdditionalCondition, uerDataParseRequestSuccess, uerId],
    );
    const isUerClassifierFieldDisabled = useMemo(
        () => !isConstructionTypesAdditionalCondition && areControlsDisabled,
        [areControlsDisabled, isConstructionTypesAdditionalCondition],
    );
    const projectHasPriceListRatio = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasPriceListRatio ?? true,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasPriceListRatio],
    );

    const objectIdsArrEditMutation = useMemo(
        () => [checkedUerRow?.workOrderObjectId],
        [checkedUerRow?.workOrderObjectId],
    );

    const successUpdateHandler = useCallback(() => {
        refreshTable?.(objectIdsArrEditMutation);
        closeModalHandler?.();
        resetModal();
    }, [closeModalHandler, objectIdsArrEditMutation, refreshTable, resetModal]);

    const [, updateWorkOrderChangeUerStatus, , updateWorkOrderChangeUerRequest] = useRequest({
        requestId: 'updateWorkOrderChangeUer',
        query: updateWorkOrderChangeUerGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const saveChangesHandler = useCallback(
        (submitData) => {
            let input = prepareUpdateUerData(
                uerId,
                submitData,
                uerRowItemStatus,
                checkboxValues,
                uerDataParseRequestSuccess,
                currentEstimateBasisData,
            );

            if (parsedUerData) {
                const { section, group } = parsedUerData?.data || {};

                input = { ...input, section, group };
            }

            const { groupName: uerRowGroup, sectionName: uerRowSection } =
                uerRowData?.uerPriceListUerRowWithRatio?.data ?? {};

            const variables: { workOrderChangePriceId: any; input: PriceListUerRowUpdateInput } = {
                workOrderChangePriceId,
                input: {
                    ...input,
                    group: uerRowGroup,
                    section: uerRowSection,
                },
            };

            dispatch(updateWorkOrderChangeUerRequest(variables));
        },
        [
            checkboxValues,
            currentEstimateBasisData,
            dispatch,
            parsedUerData,
            uerDataParseRequestSuccess,
            uerId,
            uerRowData?.uerPriceListUerRowWithRatio?.data,
            uerRowItemStatus,
            updateWorkOrderChangeUerRequest,
            workOrderChangePriceId,
        ],
    );

    return (
        <ModalDialog
            title="Редактирование расценки"
            onConfirm={handleSubmit(saveChangesHandler)}
            onClose={closeModalHandler}
            isOpen={isOpened}
            withTitleCloseButton
            hasOverlay
            btnConfirmDisabled={btnConfirmDisabled}
            isCommit={
                updateWorkOrderChangeUerStatus === RequestStatus.FETCHING || parsedUerStatus === RequestStatus.FETCHING
            }
            isOnClickOutsideDisable
            style={{ width: '600px' }}
        >
            <EditUerForm
                isDataFetching={uerRowDataStatus === RequestStatus.FETCHING}
                isUnableToLoad={false}
                uerId={uerId}
                isFileSelectorVisible
                isFileSelectorLoading={parsedUerStatus === RequestStatus.FETCHING}
                onFileSelectorFilesChange={onUploadUerFile}
                activeTab={activeFormTab}
                onTabChange={onTabChange}
                tabItems={formItems}
                control={control}
                fieldsToCheck={fieldsToCheck}
                uerClassifierItems={uerClassifierItems}
                isUerClassifierFieldDisabled={isUerClassifierFieldDisabled}
                uerChaptersItems={uerChaptersItems}
                isChapterFieldDisabled={isChapterFieldDisabled}
                areControlsDisabled={areControlsDisabled}
                contractConstructionTypes={contractConstructionTypes}
                onConstructionTypeCheckChange={onConstructionTypeCheckChange}
                checkIsConstructionTypeCheckboxCheckedHandler={checkIsConstructionTypeCheckboxChecked}
                checkIsConstructionTypeCheckboxDisabledHandler={checkIsConstructionTypeCheckboxDisabled}
                checkIsConstructionTypeEditableHandler={checkIsConstructionTypeEditable}
                uerCode={uerCode}
                ratioGroupIdsControl={ratioGroupIdsControl}
                ratioIdsControl={ratioIdsControl}
                ratioItemsUsedIds={ratioItemsUsedIds}
                dismantlingCoefControl={dismantlingCoefControl}
                hasPriceListRatio={projectHasPriceListRatio}
                setValue={setValue}
                hasWorkKind={hasWorkKind}
            />
            <UploadStatus
                isOpened={wrongParseUerParameters}
                handleCloseModal={onCloseParseErrorModal}
                statusText="Параметры из файла не совпадают с параметрами расценки."
                titleText="Проверка Сметы-Основания"
                status="error"
            />
        </ModalDialog>
    );
};

EditWorkOrderChangeUerDialog.displayName = 'EditWorkOrderChangeUerDialog';
